/**
 * Contract API Hydration Configuration
 *
 * This file defines the available hydration fields that can be used when fetching
 * contracts from the API. When hydration is requested, the API returns full objects
 * instead of just ID references.
 */

/**
 * Enum of all available hydration fields for the Contract API
 */
export enum ContractHydrationField {
  // Vendor Fields
  DIRECT_VENDOR = 'directVendor',
  PARENT_PARTY = 'parentParty',
  OTHER_PARTIES = 'otherParties',

  // Person Fields
  PARTY_CONTRACT_COORDINATOR = 'partyContractCoordinator',
  PARTIES_SIGNATORY = 'partiesSignatory',
  ORG_CONTRACT_ADMIN = 'orgContractAdmin',
  ORG_CONTRACT_SIGNER = 'orgContractSigner',
  ORG_CONTRACT_COORDINATOR = 'orgContractCoordinator',
  ORG_CONTRACT_REVIEWER = 'orgContractReviewer',
  ORG_NOTIFICATION_CONTACT = 'orgNotificationContact',

  // Other Entity Fields
  LEGAL_ENTITIES = 'legalEntities',
  SITES = 'sites',
  DIRECT_BAA = 'directBaa',
  DIRECT_COI = 'directCoi'
}

/**
 * Array of all valid hydration field names for convenience
 */
export const ALL_CONTRACT_HYDRATION_FIELDS: ContractHydrationFieldValue[] = Object.values(ContractHydrationField);

/**
 * Predefined hydration field sets for common use cases
 */
export const ContractHydrationPresets: Record<string, ContractHydrationFieldValue[]> = {
  /**
   * Hydrates all vendor-related fields
   */
  VENDORS: [
    ContractHydrationField.DIRECT_VENDOR,
    ContractHydrationField.PARENT_PARTY,
    ContractHydrationField.OTHER_PARTIES
  ] as ContractHydrationFieldValue[],

  /**
   * Hydrates all person-related fields
   */
  PEOPLE: [
    ContractHydrationField.PARTY_CONTRACT_COORDINATOR,
    ContractHydrationField.PARTIES_SIGNATORY,
    ContractHydrationField.ORG_CONTRACT_ADMIN,
    ContractHydrationField.ORG_CONTRACT_SIGNER,
    ContractHydrationField.ORG_CONTRACT_COORDINATOR,
    ContractHydrationField.ORG_CONTRACT_REVIEWER,
    ContractHydrationField.ORG_NOTIFICATION_CONTACT
  ] as ContractHydrationFieldValue[],

  /**
   * Hydrates all entity-related fields
   */
  ENTITIES: [
    ContractHydrationField.LEGAL_ENTITIES,
    ContractHydrationField.SITES,
    ContractHydrationField.DIRECT_BAA,
    ContractHydrationField.DIRECT_COI
  ] as ContractHydrationFieldValue[],

  /**
   * Hydrates all available fields
   */
  ALL: [...ALL_CONTRACT_HYDRATION_FIELDS] as ContractHydrationFieldValue[],

  /**
   * Minimal hydration for basic contract viewing
   */
  BASIC: [
    ContractHydrationField.DIRECT_VENDOR,
    ContractHydrationField.ORG_CONTRACT_ADMIN
  ] as ContractHydrationFieldValue[],

  /**
   * Full hydration for detailed contract viewing/editing
   */
  FULL: [...ALL_CONTRACT_HYDRATION_FIELDS] as ContractHydrationFieldValue[]
};

/**
 * Type for hydration field values
 */
export type ContractHydrationFieldValue = ContractHydrationField | string;
